import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Shield, BarChart3, MessageSquareWarning, Fingerprint, Activity } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { PATTERN_LABELS } from "@/lib/tacticalDescriptions";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";

const BAR_COLORS = [
  "hsl(184, 100%, 50%)", "hsl(184, 80%, 42%)", "hsl(184, 60%, 36%)",
  "hsl(184, 45%, 30%)", "hsl(0, 0%, 40%)", "hsl(0, 0%, 35%)",
  "hsl(0, 0%, 30%)", "hsl(0, 0%, 28%)",
];

interface NeutRow {
  created_at: string;
  toxicity_score: number;
  manipulation_primary_type: string | null;
  category: string;
  raw_text: string;
}

function humanLabel(key: string): string {
  return PATTERN_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const TrendsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<NeutRow[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [rehearseLoading, setRehearseLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      setLoading(true);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("neutralizations")
        .select("created_at, toxicity_score, manipulation_primary_type, category, raw_text")
        .eq("user_id", user.id)
        .gte("created_at", thirtyDaysAgo)
        .order("created_at", { ascending: true });
      setRows((data as NeutRow[]) || []);
      setLoading(false);
    };
    fetch();
  }, [user]);

  const filtered = useMemo(() => {
    if (categoryFilter === "all") return rows;
    return rows.filter((r) => r.category === categoryFilter);
  }, [rows, categoryFilter]);

  const totalAnalyses = filtered.length;

  // ─── Intelligence computations ─────────────────────────────────

  const tacticCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach((r) => {
      if (r.manipulation_primary_type) counts[r.manipulation_primary_type] = (counts[r.manipulation_primary_type] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  const topTactic = tacticCounts.length ? humanLabel(tacticCounts[0][0]) : "N/A";

  // Ambiguous vs high-signal split
  const signalMix = useMemo(() => {
    const AMBIGUOUS_TYPES = new Set(["neutral_accountability", "ambiguous_responsibility", "soft_invalidation"]);
    let ambiguous = 0, highSignal = 0;
    filtered.forEach((r) => {
      if (!r.manipulation_primary_type) return;
      if (AMBIGUOUS_TYPES.has(r.manipulation_primary_type) || Number(r.toxicity_score) < 5) {
        ambiguous++;
      } else {
        highSignal++;
      }
    });
    return { ambiguous, highSignal, total: ambiguous + highSignal };
  }, [filtered]);

  // Recurring pattern count (tactics that appear 2+)
  const recurringPatternCount = useMemo(() => tacticCounts.filter(([, c]) => c >= 2).length, [tacticCounts]);

  // Daily signal activity
  const dailyData = useMemo(() => {
    const dayMap: Record<string, number[]> = {};
    filtered.forEach((r) => {
      const day = new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      if (!dayMap[day]) dayMap[day] = [];
      dayMap[day].push(Number(r.toxicity_score));
    });
    return Object.entries(dayMap).map(([date, scores]) => ({
      date,
      avg: +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1),
      count: scores.length,
    }));
  }, [filtered]);

  // Tactics bar data (top 8)
  const tacticBarData = useMemo(() =>
    tacticCounts.slice(0, 8).map(([key, value]) => ({
      name: humanLabel(key),
      value,
    })),
  [tacticCounts]);

  // Common phrases with associated pattern + context
  const commonPhrases = useMemo(() => {
    const phraseData: Record<string, { count: number; pattern: string | null; contexts: Set<string> }> = {};
    filtered.forEach((r) => {
      const text = r.raw_text?.toLowerCase() || "";
      const knownPhrases = [
        "you're too sensitive", "i never said that", "you always", "you never",
        "calm down", "it's not a big deal", "you're overreacting", "i was just joking",
        "that never happened", "you're crazy", "no one else thinks that",
        "if you really loved me", "after everything i've done", "you made me do it",
        "you're being dramatic", "i'm sorry you feel that way",
      ];
      knownPhrases.forEach((p) => {
        if (text.includes(p)) {
          if (!phraseData[p]) phraseData[p] = { count: 0, pattern: null, contexts: new Set() };
          phraseData[p].count++;
          if (r.manipulation_primary_type) phraseData[p].pattern = r.manipulation_primary_type;
          if (r.category) phraseData[p].contexts.add(r.category);
        }
      });
    });
    return Object.entries(phraseData)
      .map(([phrase, data]) => [phrase, { ...data, contexts: Array.from(data.contexts) }] as const)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 8);
  }, [filtered]);

  // Category split
  const categorySplit = useMemo(() => {
    const work = rows.filter((r) => r.category === "work");
    const personal = rows.filter((r) => r.category === "personal");
    const pub = rows.filter((r) => r.category === "public");
    const avg = (arr: NeutRow[]) => arr.length ? +(arr.reduce((s, r) => s + Number(r.toxicity_score), 0) / arr.length).toFixed(1) : 0;
    const topPattern = (arr: NeutRow[]) => {
      const counts: Record<string, number> = {};
      arr.forEach((r) => { if (r.manipulation_primary_type) counts[r.manipulation_primary_type] = (counts[r.manipulation_primary_type] || 0) + 1; });
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      return sorted.length ? humanLabel(sorted[0][0]) : null;
    };
    return [
      { label: "Work", count: work.length, avg: avg(work), top: topPattern(work) },
      { label: "Personal", count: personal.length, avg: avg(personal), top: topPattern(personal) },
      { label: "Public", count: pub.length, avg: avg(pub), top: topPattern(pub) },
    ];
  }, [rows]);

  // ─── Interpretive insight line ─────────────────────────────────

  const insightLine = useMemo(() => {
    if (totalAnalyses < 3) return "Analyzing early signals — more data will sharpen your pattern intelligence.";
    const topEntry = tacticCounts[0];
    if (!topEntry) return null;
    const label = humanLabel(topEntry[0]);
    const pct = Math.round((topEntry[1] / totalAnalyses) * 100);
    if (signalMix.ambiguous > signalMix.highSignal) {
      return `Most submissions fall into the ambiguous/moderate range. ${label} is the most recurring pattern (${pct}%).`;
    }
    return `${label} is the most recurring pattern at ${pct}% of submissions.`;
  }, [totalAnalyses, tacticCounts, signalMix]);

  // Environment insight
  const envInsight = useMemo(() => {
    const withData = categorySplit.filter((c) => c.count >= 3);
    if (withData.length < 2) return null;
    const sorted = [...withData].sort((a, b) => b.avg - a.avg);
    if (sorted[0].avg - sorted[sorted.length - 1].avg >= 1.5) {
      return `${sorted[0].label} submissions trend more confrontational than ${sorted[sorted.length - 1].label}.`;
    }
    return null;
  }, [categorySplit]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <IntelligenceTopBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <IntelligenceTopBar />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <PageBreadcrumb items={[{ label: "Dashboard", path: "/dashboard" }, { label: "Insights" }]} />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="text-foreground">Pattern </span>
              <span className="text-cyan">Intelligence</span>
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-muted-foreground text-sm">
                30-day behavioral pattern overview · {rows.length} total submissions
              </p>
              <button
                disabled={rehearseLoading}
                onClick={() => {
                  const top3 = tacticCounts.slice(0, 3).map(([key, count]) => ({ name: humanLabel(key), count }));
                  navigate("/rehearsal", {
                    state: {
                      entry_type: "pattern_history",
                      patterns: top3,
                    },
                  });
                }}
                className="text-[11px] px-3 py-1 rounded-lg border border-border text-muted-foreground hover:border-cyan/40 hover:text-cyan transition-colors font-medium whitespace-nowrap disabled:opacity-50"
              >
                Rehearse My Patterns (2 Credits)
              </button>
            </div>
          </div>

          <ToggleGroup
            type="single"
            value={categoryFilter}
            onValueChange={(v) => v && setCategoryFilter(v)}
            className="bg-muted/50 rounded-lg p-0.5"
          >
            {["all", "work", "personal", "public"].map((cat) => (
              <ToggleGroupItem
                key={cat}
                value={cat}
                className="text-xs px-3 py-1 capitalize data-[state=on]:bg-cyan/15 data-[state=on]:text-cyan"
              >
                {cat === "all" ? "All" : cat}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        {totalAnalyses === 0 ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/50 border border-border mx-auto">
              <BarChart3 className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <div className="space-y-1.5 max-w-sm mx-auto">
              <h3 className="text-lg font-semibold text-foreground">No Insights Yet</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Insights tracks your behavioral patterns over time — recurring tactics, signal intensity, and environment breakdowns. Run a few analyses to start building your intelligence profile.
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground hover:scale-[1.02] transition-transform"
            >
              <Shield className="w-4 h-4" />
              Start Analyzing
            </button>
            <p className="text-xs text-muted-foreground/60">Viewing Insights is always free</p>
          </div>
        ) : (
          <>
            {/* Intelligence Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-border bg-card">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-cyan/10">
                    <Shield className="w-5 h-5 text-cyan" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Recurring Pattern</p>
                    <p className="text-lg font-bold text-foreground truncate">{topTactic}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-card">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-amber-500/10">
                    <Activity className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Signal Clarity</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-cyan" />
                        <span className="text-xs font-medium text-foreground">{signalMix.highSignal}</span>
                        <span className="text-[10px] text-muted-foreground">high-signal</span>
                      </div>
                      <span className="text-muted-foreground/40">·</span>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-amber-400/70" />
                        <span className="text-xs font-medium text-foreground">{signalMix.ambiguous}</span>
                        <span className="text-[10px] text-muted-foreground">ambiguous</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-card">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-cyan/10">
                    <Fingerprint className="w-5 h-5 text-cyan" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Recurring Clusters</p>
                    <p className="text-2xl font-bold text-foreground">
                      {recurringPatternCount}
                      <span className="text-sm text-muted-foreground font-normal ml-1">
                        {recurringPatternCount === 1 ? "pattern" : "patterns"}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Interpretive insight */}
            {insightLine && (
              <div className="px-4 py-3 rounded-lg border border-cyan/15 bg-cyan/5">
                <p className="text-sm text-foreground/80 font-mono">
                  <span className="text-cyan mr-2">▸</span>
                  {insightLine}
                </p>
              </div>
            )}

            {/* Pattern Activity Over Time — only show if 3+ days of data */}
            {dailyData.length >= 3 && (
            <Card className="border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-cyan" />
                  Pattern Signal Trend
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Average signal intensity per day</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dailyData}>
                    <XAxis dataKey="date" tick={{ fill: "hsl(0, 0%, 55%)", fontSize: 11 }} axisLine={{ stroke: "hsl(0, 0%, 20%)" }} />
                    <YAxis domain={[0, 10]} tick={{ fill: "hsl(0, 0%, 55%)", fontSize: 11 }} axisLine={{ stroke: "hsl(0, 0%, 20%)" }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "hsl(0, 0%, 12%)", border: "1px solid hsl(0, 0%, 20%)", borderRadius: 8, color: "hsl(0, 0%, 90%)" }}
                      formatter={(value: number) => [`${value}/10`, "Avg Signal"]}
                    />
                    <Line type="monotone" dataKey="avg" stroke="hsl(184, 100%, 50%)" strokeWidth={2} dot={{ fill: "hsl(184, 100%, 50%)", r: 3 }} activeDot={{ r: 5, fill: "hsl(184, 100%, 60%)" }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            )}

            {/* Two-column: Tactics + Phrases */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Tactics Distribution */}
              <Card className="border-border bg-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-4 h-4 text-cyan" />
                    Pattern Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tacticBarData.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-8 text-center">No patterns detected yet.</p>
                  ) : (
                    <div className="space-y-2 pt-1">
                      {tacticBarData.map((item, i) => {
                        const maxVal = tacticBarData[0]?.value || 1;
                        const pct = Math.round((item.value / maxVal) * 100);
                        return (
                          <div key={item.name} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground font-mono w-[160px] shrink-0 leading-tight">{item.name}</span>
                            <div className="flex-1 h-4 bg-muted/20 rounded-sm overflow-hidden">
                              <div
                                className="h-full rounded-sm transition-all"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
                                }}
                              />
                            </div>
                            <span className="text-xs font-mono text-foreground/70 w-6 text-right shrink-0">{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Common Phrases */}
              <Card className="border-border bg-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquareWarning className="w-4 h-4 text-destructive" />
                    Recurring Phrase Clusters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {commonPhrases.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-8 text-center">No recurring phrases detected yet. More analyses will reveal clusters.</p>
                  ) : (
                    <div className="space-y-2 pt-1">
                      {commonPhrases.map(([phrase, data]) => {
                        const ctxNote = data.contexts.length >= 2
                          ? `Seen in ${data.contexts.join(" and ")} contexts`
                          : data.contexts.length === 1
                          ? `Seen mostly in ${data.contexts[0]} contexts`
                          : null;
                        return (
                          <div
                            key={phrase}
                            className="px-3 py-2.5 rounded-lg bg-muted/20 border border-border space-y-1"
                          >
                            <div className="flex items-start gap-3">
                              <span className="text-sm text-foreground/90 flex-1">
                                "{phrase}"
                              </span>
                              <div className="flex items-center gap-2 shrink-0">
                                {data.pattern && (
                                  <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                                    {humanLabel(data.pattern)}
                                  </span>
                                )}
                                <span className="text-[10px] font-bold bg-destructive/15 text-destructive rounded-full px-2 py-0.5">
                                  {data.count}×
                                </span>
                              </div>
                            </div>
                            {ctxNote && (
                              <p className="text-[10px] text-muted-foreground/60 font-mono pl-0.5">{ctxNote}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Environment Comparison */}
            <Card className="border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Environment Comparison</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  {categorySplit.map((cat) => (
                    <div key={cat.label} className="text-center p-4 rounded-lg bg-muted/30 border border-border">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">{cat.label}</p>
                      {cat.count < 2 ? (
                        <p className="text-sm text-muted-foreground/60 mt-2">
                          {cat.count === 0 ? "No data yet" : "Early signal only"}
                        </p>
                      ) : (
                        <>
                          <p className="text-xl font-bold text-foreground">
                            {cat.avg}<span className="text-xs text-muted-foreground">/10</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{cat.count} submissions</p>
                          {cat.top && (
                            <p className="text-[10px] text-cyan/70 font-mono mt-1 truncate">{cat.top}</p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {envInsight && (
                  <p className="text-xs text-foreground/60 font-mono px-1">
                    <span className="text-cyan mr-1.5">▸</span>
                    {envInsight}
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default TrendsDashboard;
